import { useState } from "react";
import Select from "react-select";
import { H3, Label } from "../Text";
import { toast } from "react-toastify";
import Circle from "../Animation/Circle";
import { Button } from "../Buttons";
import { Request } from "../../hooks/Request";
import TableComplete from "../Tables/TableComplete";
import { requestAuth } from "../services/RequestService";
import { useDialog } from "../../contexts/DialogContext";
import { DialogConfirmation } from "../Modal";
import { numberWithDecimals } from "../../utils/validate";
import { FileManagerModal } from "./FileManagerModal";
import { ComparativeTransportPdf } from "../common/ComparativeTransportPdf";
import { useFormikContext } from "formik";

const getQuoteMeta = (item) => {
  if (!item?.description2) return {};
  try {
    return JSON.parse(item.description2);
  } catch {
    return {};
  }
};

const InputSubDataTransportAdd = ({ field }) => {
  const { openDialog, dialogClose } = useDialog();
  const { values: formValues } = useFormikContext();

  const datosGenerales = (() => {
    try { return JSON.parse(formValues["datosGeneralesDeTransporte"] || "{}"); } catch { return {}; }
  })();

  const mainFormGeneralData = {
    almacenDestino:
      datosGenerales["almacénDestino"] ||
      formValues["almacénDestino"] ||
      "",
    tiempoEntrega:
      datosGenerales["tiempoEntrega(Días)"] ||
      formValues["tiempoEntrega(Días)"] ||
      "",
    cantidad:
      datosGenerales["cantidad"] ||
      formValues["cantidad"] ||
      "",
  };

  // Fallback: cargar datos generales guardados si no están en el form actual
  const { data: storedGeneralData } = Request({
    urlApi: `/Procedure/${field.id}/dataValues?fieldnames=datosGeneralesDeTransporte`,
  });

  const storedParsed = (() => {
    try {
      return JSON.parse(storedGeneralData?.datosGeneralesDeTransporte || "{}");
    } catch {
      return {};
    }
  })();

  if (storedParsed["almacénDestino"] && !mainFormGeneralData.almacenDestino) {
    mainFormGeneralData.almacenDestino = storedParsed["almacénDestino"];
  }
  if (storedParsed["tiempoEntrega(Días)"] && !mainFormGeneralData.tiempoEntrega) {
    mainFormGeneralData.tiempoEntrega = storedParsed["tiempoEntrega(Días)"];
  }
  if (storedParsed["cantidad"] && !mainFormGeneralData.cantidad) {
    mainFormGeneralData.cantidad = storedParsed["cantidad"];
  }

  // 1. Cargar las cotizaciones ya registradas
  const {
    data: quotesData,
    loading: loadingQuotes,
    call: reloadQuotes,
  } = Request({
    urlApi: `/dataSet/procedure/${field.id}/field/${field.fieldId}/subdata`,
  });

  // 2. Cargar proveedores desde el servicio (field.urlApi)
  const { data: providers, loading: loadingProviders } = Request({
    urlApi: field.urlApi,
  });

  function handleDocPdf(procedureId) {
    openDialog(<ComparativeTransportPdf procedureId={procedureId} />);
  }

  const handleApprove = async (selectedItem) => {
    const isCurrentlyApproved = selectedItem.description3 === "true";
    try {
      if (!isCurrentlyApproved) {
        const approvedQuotes = quotesData.filter(
          (q) => q.description3 === "true" && q.id !== selectedItem.id,
        );
        for (const quote of approvedQuotes) {
          await requestAuth("put", `/dataSet/procedure/subData/${quote.id}`, {
            ...quote,
            Description3: "false",
            dataSetId: field.dataSetId,
          });
        }
      }

      await requestAuth(
        "put",
        `/dataSet/procedure/subData/${selectedItem.id}`,
        {
          ...selectedItem,
          Description3: isCurrentlyApproved ? "false" : "true",
          dataSetId: field.dataSetId,
        },
      );

      toast.success(
        isCurrentlyApproved ? "Aprobación removida" : "Proveedor aprobado",
      );
      reloadQuotes();
    } catch (error) {
      toast.error("Error al procesar la aprobación");
    }
  };

  function handleForm(item = null) {
    openDialog(
      <div className="w-[500px] max-w-full">
        {loadingProviders ? (
          <FormAddQuote
            existingQuote={item}
            providers={providers || []}
            field={field}
            generalData={mainFormGeneralData}
            quotesData={quotesData || []}
            reload={reloadQuotes}
            onClose={dialogClose}
          />
        ) : (
          <Circle />
        )}
      </div>,
    );
  }

  function handleDelete(data) {
    openDialog(
      <DialogConfirmation
        children={<H3>¿Eliminar esta cotización?</H3>}
        onClose={dialogClose}
        method="delete"
        url={`/dataSet/procedure/subData/${data.id}`}
        texBtn="Eliminar"
        handleSuccess={reloadQuotes}
      />,
    );
  }

  function handleFiles(item) {
    openDialog(
      <FileManagerModal
        item={item}
        reload={reloadQuotes}
        onClose={dialogClose}
      />,
    );
  }

  return (
    <div className="bg-gray-100 p-4 rounded-xl shadow-inner">
      <div className="flex justify-between items-center mb-4">
        <div className="flex gap-2 items-center">
          <Label className="text-lg font-bold text-gray-700">
            {field.labelCustom || field.label}
          </Label>
          <Button
            onClick={() => handleDocPdf(field.id)}
            className="text-sm py-1"
          >
            <i className="fas fa-file-pdf"></i>
          </Button>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => handleForm(null)} className="text-sm py-1">
            + Registrar Oferta
          </Button>
        </div>
      </div>
      {loadingQuotes ? (
        <TableComplete
          header={[
            {
              name: "label",
              label: "Proveedor",
              type: "custom",
              builder: (val, _, item) => (
                <div className="flex flex-col">
                  <span
                    className={`font-bold ${item.description3 === "true" ? "text-green-600" : ""}`}
                  >
                    {val}
                  </span>
                  <span className="text-[10px] text-gray-400">
                    ID: {item.id}
                  </span>
                </div>
              ),
            },
            {
              name: "numberValue",
              label: "Total Ofertado",
              type: "custom",
              builder: (val, _, item) => {
                const meta = item.description2
                  ? JSON.parse(item.description2)
                  : {};
                const qty = meta.cantidad || mainFormGeneralData.cantidad || 1;
                return (
                  <span className="font-mono text-blue-700 font-bold">
                      {numberWithDecimals(
                      Number(meta.precio || val || 0) * parseFloat(qty),
                    )}
                  </span>
                );
              },
            },
            {
              name: "almacénDestino",
              label: "Almacén Destino",
              type: "custom",
              builder: () => mainFormGeneralData.almacenDestino || "-",
            },
            {
              name: "description3",
              label: "Estado",
              type: "custom",
              builder: (_, __, item) => (
                <button
                  onClick={() => handleApprove(item)}
                  className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase transition-colors ${item.description3 === "true"
                      ? "bg-green-500 text-white"
                      : "bg-gray-200 text-gray-500 hover:bg-green-100 hover:text-green-600"
                    }`}
                >
                  {item.description3 === "true"
                    ? "Seleccionado"
                    : "Seleccionar"}
                </button>
              ),
            },
            {
              name: "id",
              type: "action",
              label: "Acciones",
              actions: [
                {
                  icon: "fas fa-edit",
                  color: "text-blue-500",
                  action: handleForm,
                },
                {
                  icon: "fas fa-paperclip",
                  color: "text-purple-500",
                  action: handleFiles,
                },
                {
                  icon: "fas fa-trash",
                  color: "text-red-400",
                  action: handleDelete,
                },
              ],
            },
          ]}
          data={quotesData}
        />
      ) : (
        <Circle />
      )}
    </div>
  );
};

const FormAddQuote = ({
  existingQuote,
  providers,
  field,
  generalData,
  quotesData,
  reload,
  onClose,
}) => {
  const meta = getQuoteMeta(existingQuote);

  const currentGeneralData = {
    almacenDestino: generalData?.almacenDestino || meta.almacenDestino || "",
    tiempoEntrega: generalData?.tiempoEntrega || meta.tiempoEntrega || "",
    cantidad: generalData?.cantidad || meta.cantidad || "",
  };

  const { data: paymentTermsList } = Request({
    urlApi: "/Dropdown/terminos_pago_dropdown/options",
  });

  const paymentTermsOptions = Array.isArray(paymentTermsList)
    ? paymentTermsList.map(opt => ({
        value: opt.name || opt.label || opt.id || "",
        label: opt.name || opt.label || opt.id || "",
      })).filter(opt => opt.value)
    : [];

  const [formData, setFormData] = useState({
    providerName: existingQuote?.label || meta.providerName || "",
    precio: existingQuote?.numberValue || meta.precio || 0,
    formaPago: meta.formaPago || "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  async function handleSubmit() {
    if (!formData.providerName) {
      toast.error("Seleccione un proveedor");
      return;
    }

    if (Number(formData.precio) <= 0) {
      toast.error("El precio ofertado debe ser mayor a 0");
      return;
    }

    if (!currentGeneralData.almacenDestino) {
      toast.error("Complete almacén destino en Información Operativa");
      return;
    }

    const targetUrl = existingQuote
      ? `/dataSet/procedure/subData/${existingQuote.id}`
      : `/dataSet/procedure/${field.id}/field/${field.fieldId}/subdata`;

    const descriptionData = {
      ...meta,
      ...formData,
      ...currentGeneralData,
      cantidad: currentGeneralData.cantidad || "1",
      fechaRegistro: meta.fechaRegistro || new Date().toISOString(),
    };

    const payload = {
      Label: formData.providerName,
      NumberValue: Number(formData.precio),
      Description2: JSON.stringify(descriptionData),
      Description3: existingQuote?.description3 || "false",
      Description4: existingQuote?.description4 || "[]",
      dataSetId: field.dataSetId,
    };

    try {
      await requestAuth(existingQuote ? "put" : "post", targetUrl, payload);

      await Promise.all(
        quotesData
          .filter((quote) => quote.id !== existingQuote?.id)
          .map((quote) => {
            const metaQuote = getQuoteMeta(quote);

            return requestAuth("put", `/dataSet/procedure/subData/${quote.id}`, {
              ...quote,
              Description2: JSON.stringify({
                ...metaQuote,
                ...currentGeneralData,
              }),
              dataSetId: field.dataSetId,
            });
          }),
      );

      toast.success("Datos guardados");
      reload();
      onClose();
    } catch (err) {
      toast.error("Error al guardar");
    }
  }

  return (
    <div className="p-6 bg-white rounded-lg flex flex-col gap-4">
      <H3 className="border-b pb-2 text-gray-700">Registro de Cotización</H3>

      <div className="grid grid-cols-1 gap-4">
        <div className="flex flex-col gap-1">
          <Label>Proveedor</Label>
          <Select
            placeholder="Seleccione un proveedor..."
            isSearchable
            isClearable
            options={providers.map((p) => ({
              value: p.name,
              label: p.name,
            }))}
            value={
              formData.providerName
                ? { value: formData.providerName, label: formData.providerName }
                : null
            }
            onChange={(selectedOption) => {
              setFormData((prev) => ({
                ...prev,
                providerName: selectedOption ? selectedOption.value : "",
              }));
            }}
            styles={{
              control: (base) => ({
                ...base,
                border: "1px solid #e2e8f0",
                borderRadius: "0.375rem",
                padding: "2px",
              }),
            }}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <Label>Precio Ofertado</Label>
            <input
              type="number"
              name="precio"
              value={formData.precio}
              onChange={handleChange}
              className="p-2 border rounded"
              placeholder="0.00"
            />
          </div>

          <div className="flex flex-col gap-1">
            <Label>Forma de Pago</Label>
            <select
              name="formaPago"
              value={formData.formaPago}
              onChange={handleChange}
              className="p-2 border rounded"
            >
              <option value="">Seleccionar forma de pago</option>
              {paymentTermsOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <button
          onClick={onClose}
          className="px-4 py-2 text-gray-400 hover:text-gray-600"
        >
          Cancelar
        </button>

        <Button onClick={handleSubmit}>Guardar Cotización</Button>
      </div>
    </div>
  );
};

export default InputSubDataTransportAdd;